/**
 * Debug Media Utility
 * Loads test video and audio streams for testing player without WebRTC
 */

const TEST_VIDEO_URL = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
const TEST_AUDIO_URL = 'https://archive.org/download/OldRunescapeSoundtrack/Sea%20Shanty2.mp3';

interface DebugMediaStreams {
  videoStream: MediaStream | null;
  audioStream: MediaStream | null;
  combined: MediaStream | null;
  cleanup: () => void;
}

/**
 * Create a MediaStream from a video element
 */
async function createVideoStream(url: string): Promise<{ stream: MediaStream; element: HTMLVideoElement }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.src = url;
    video.crossOrigin = 'anonymous';
    video.loop = true;
    video.muted = true; // Start muted for autoplay
    video.playsInline = true;

    video.addEventListener('loadedmetadata', async () => {
      try {
        await video.play();

        // Capture video stream
        const stream = (video as HTMLVideoElement & { captureStream: () => MediaStream }).captureStream();

        if (!stream) {
          reject(new Error('Failed to capture video stream'));
          return;
        }

        console.log('[DebugMedia] Video stream created:', stream.getTracks());
        resolve({ stream, element: video });
      } catch (error) {
        reject(error);
      }
    });

    video.addEventListener('error', (e) => {
      reject(new Error(`Failed to load video: ${video.error?.message}`));
    });
  });
}

/**
 * Create a MediaStream from an audio element
 */
async function createAudioStream(url: string): Promise<{ stream: MediaStream; element: HTMLAudioElement }> {
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.src = url;
    audio.crossOrigin = 'anonymous';
    audio.loop = true;

    audio.addEventListener('loadedmetadata', async () => {
      try {
        await audio.play();

        // Create audio context to capture stream
        const audioContext = new AudioContext();
        const source = audioContext.createMediaElementSource(audio);
        const destination = audioContext.createMediaStreamDestination();
        source.connect(destination);

        // NOTE: We do NOT connect to audioContext.destination here!
        // The Player component will play the audio from the stream.
        // Connecting here would cause duplicate audio (playing twice).

        const stream = destination.stream;

        console.log('[DebugMedia] Audio stream created:', stream.getTracks());
        resolve({ stream, element: audio });
      } catch (error) {
        reject(error);
      }
    });

    audio.addEventListener('error', (e) => {
      reject(new Error(`Failed to load audio: ${audio.error?.message}`));
    });
  });
}

/**
 * Load debug media streams
 * Returns video stream, audio stream, and a combined stream
 */
export async function loadDebugMediaStreams(): Promise<DebugMediaStreams> {
  console.log('[DebugMedia] Loading test media streams...');

  try {
    // Load video and audio in parallel
    const [videoResult, audioResult] = await Promise.all([
      createVideoStream(TEST_VIDEO_URL),
      createAudioStream(TEST_AUDIO_URL),
    ]);

    // Video stream should have ONLY video tracks (no audio)
    const videoOnly = new MediaStream(videoResult.stream.getVideoTracks());

    // Audio stream should have ONLY audio tracks
    const audioOnly = new MediaStream(audioResult.stream.getAudioTracks());

    console.log('[DebugMedia] Separate streams created:', {
      videoTracks: videoOnly.getVideoTracks().length,
      audioTracks: audioOnly.getAudioTracks().length,
    });

    // Cleanup function
    const cleanup = () => {
      console.log('[DebugMedia] Cleaning up media streams');

      // Stop all tracks
      videoResult.stream.getTracks().forEach(track => track.stop());
      audioResult.stream.getTracks().forEach(track => track.stop());
      videoOnly.getTracks().forEach(track => track.stop());
      audioOnly.getTracks().forEach(track => track.stop());

      // Pause and remove elements
      videoResult.element.pause();
      audioResult.element.pause();
      videoResult.element.remove();
      audioResult.element.remove();
    };

    return {
      videoStream: videoOnly,
      audioStream: audioOnly,
      combined: null, // Not used anymore - we inject separately
      cleanup,
    };
  } catch (error) {
    console.error('[DebugMedia] Failed to load media streams:', error);
    return {
      videoStream: null,
      audioStream: null,
      combined: null,
      cleanup: () => {
        // No cleanup needed for failed load
      },
    };
  }
}

/**
 * Create a simple test pattern video stream (canvas-based)
 * Fallback if loading external media fails
 */
export function createTestPatternStream(): MediaStream {
  console.log('[DebugMedia] Creating test pattern stream');

  const canvas = document.createElement('canvas');
  canvas.width = 1280;
  canvas.height = 720;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas 2d context');
  }

  let frame = 0;

  // Draw animated test pattern
  function drawTestPattern() {
    if (!ctx) return;

    // Background
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Animated circle
    const time = frame / 60;
    const x = canvas.width / 2 + Math.cos(time) * 200;
    const y = canvas.height / 2 + Math.sin(time) * 200;

    ctx.fillStyle = '#e94560';
    ctx.beginPath();
    ctx.arc(x, y, 50, 0, Math.PI * 2);
    ctx.fill();

    // Text
    ctx.fillStyle = '#ffffff';
    ctx.font = '48px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('DEBUG MODE - Test Pattern', canvas.width / 2, canvas.height / 2 - 50);
    ctx.font = '24px sans-serif';
    ctx.fillText(`Frame: ${frame}`, canvas.width / 2, canvas.height / 2 + 50);

    frame++;
    requestAnimationFrame(drawTestPattern);
  }

  drawTestPattern();

  // Capture stream from canvas
  const stream = canvas.captureStream(30); // 30 FPS
  console.log('[DebugMedia] Test pattern stream created');

  return stream;
}

